import { confirmButton } from './confirm-button.ts';
import { $ } from './dom.ts';
import { apiFetch, describe, jsonIfOk, pageError } from './fetch-helpers.ts';

/*
 * Pairing chrome for assignment pages (plans/pair-code-sharing.md): the
 * "Working with X" toolbar indicator (with the detach/rejoin control) and
 * the save-conflict banner shown when a partner saved from another machine.
 * Backend-agnostic the way assignment-ui.ts is: index.ts wires the actual
 * storage behavior.
 */

export type PairingPartner = { username: string; name: string | null; detached: boolean };

export type PairingStatus =
  // `unknown`: the status check itself failed — the page behaves unpaired
  // but shows this message, since a paired student silently treated as solo
  // loses the conflict guard.
  | { paired: false; unknown?: string }
  | {
      paired: true;
      dirKey: string;
      detached: boolean;
      // This subject's own username — what their own commits carry.
      self: string;
      partners: PairingPartner[];
      unknown?: undefined;
    };

export const fetchPairingStatus = async (
  courseId: string,
  dir: string,
  student: string | null = null,
): Promise<PairingStatus> => {
  const qs = student ? `?student=${encodeURIComponent(student)}` : '';
  try {
    return await apiFetch(`/api/pairing-status/${courseId}/${dir}${qs}`).then(
      jsonIfOk<PairingStatus>,
    );
  } catch (e) {
    // A status hiccup must never break the page; it behaves unpaired (the
    // server still routes saves to the right directory) and says so.
    return { paired: false, unknown: describe(e, 'check your pairing status') };
  }
};

export const partnersLabel = (partners: PairingPartner[]): string =>
  partners.map((p) => p.name ?? p.username).join(', ') || 'your partner';

/*
 * The toolbar indicator: "Working with Ada" (following the pair) or
 * "Working solo — paired with Ada" (detached), plus the switch control.
 * `readOnly` (teacher review) shows the state without the control. Works in
 * both editor toolbars: on Snap pages (which right-align their action
 * buttons) it slots in just before them instead of at the far edge.
 */
export const showPairingIndicator = (
  status: PairingStatus & { paired: true },
  readOnly: boolean,
) => {
  const toolbar = $('.editor-toolbar') as HTMLElement | null;
  if (!toolbar) return;

  const span = document.createElement('span');
  span.className = 'pair-indicator';
  const label = document.createElement('span');
  label.textContent = status.detached
    ? `Working solo — paired with ${partnersLabel(status.partners)}`
    : `Working with ${partnersLabel(status.partners)}`;
  span.append(label);

  if (!readOnly) {
    const button = document.createElement('button');
    button.type = 'button';
    if (status.detached) {
      button.textContent = 'Rejoin pair';
      button.title =
        'You will see the pair’s shared code again (your solo copy is kept in its history)';
      confirmButton(button, 'Really rejoin?', () => switchMode('rejoin'));
    } else {
      button.textContent = 'Work on my own';
      button.title = 'You get a private copy of the current code and stop sharing until you rejoin';
      confirmButton(button, 'Really detach?', () => switchMode('detach'));
    }
    span.append(button);
  }
  const buttons = toolbar.querySelector('.toolbar-buttons');
  if (buttons) {
    buttons.before(span);
  } else {
    toolbar.append(span);
  }
};

// Runs under confirmButton, which reports a rejection (a network failure
// here) through pageError.
const switchMode = async (action: 'detach' | 'rejoin') => {
  const { courseId, dir } = pageCourseDir();
  if (!courseId || !dir) return;
  const r = await apiFetch(`/api/pairing-${action}/${courseId}/${dir}`, { method: 'POST' });
  if (r.ok) {
    window.location.reload();
  } else {
    const body = await r.json().catch(() => ({ error: `${r.status}` }));
    pageError(`Could not ${action}: ${body.error}`);
  }
};

const pageCourseDir = () => {
  const m = window.location.pathname.replace(/^\/+|\/+$/g, '').match(/^c\/([^/]+)\/(.+)$/);
  return { courseId: m?.[1] ?? null, dir: m?.[2] ?? null };
};

/*
 * The conflict banner: partner saved from another machine while this editor
 * had unsaved work. One at a time; both actions dismiss it.
 */
export const showConflictBanner = (opts: {
  partnerLabel: string;
  onLoadTheirs: () => void;
  onKeepMine: () => void;
}) => {
  dismissConflictBanner();
  // The banner sits OUTSIDE the fullscreened element (it's a sibling of the
  // frame container), so in fullscreen it — and the toolbar's "Not saved"
  // status — would be invisible while every save silently bounced off the
  // unanswered conflict. Nothing else can save until the student chooses,
  // so this is worth yanking them out of fullscreen for.
  exitFullscreen();
  // Just above the editing surface: the Monaco grid on code pages, the
  // embedded IDE on Snap pages.
  const page = ($('.editor-page') ?? $('.snap-frame-container')) as HTMLElement | null;
  if (!page) return;

  const banner = document.createElement('div');
  banner.className = 'pair-conflict-banner';

  const message = document.createElement('span');
  message.textContent = `${opts.partnerLabel} saved a newer version from another computer.`;
  banner.append(message);

  const load = document.createElement('button');
  load.type = 'button';
  load.textContent = 'Load their version';
  load.onclick = () => {
    dismissConflictBanner();
    opts.onLoadTheirs();
  };

  const keep = document.createElement('button');
  keep.type = 'button';
  keep.textContent = 'Save mine anyway';
  keep.onclick = () => {
    dismissConflictBanner();
    opts.onKeepMine();
  };

  banner.append(load, keep);
  page.before(banner);
};

export const dismissConflictBanner = () => {
  document.querySelector('.pair-conflict-banner')?.remove();
};

// Fullscreen shows only the fullscreened element, hiding banners and the
// toolbar status; leave it when something needs the student's attention
// (an unanswered conflict, a logged-out save). Exiting never needs a user
// gesture, but guard anyway — losing fullscreen must never break the caller.
export const exitFullscreen = () => {
  if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
};

/*
 * The Snap! driver/navigator overlay (plans/pair-code-sharing.md): pair
 * programming is one driver at a time, so instead of letting both students
 * edit into an endless ping-pong of save conflicts, a save that bounces off
 * the partner's newer work blocks the IDE behind a modal until this student
 * picks a role. It renders INSIDE the frame container, so it blocks the IDE
 * physically and stays visible in fullscreen — where a sibling banner would
 * be invisible while every save silently bounced. There is deliberately no
 * click-outside dismissal: not choosing means not working.
 */
export const showDriverChoice = (opts: {
  partnerLabel: string;
  onTakeOver: () => void;
  onNavigate: () => void;
}) => {
  driverOverlay(
    `You and ${opts.partnerLabel} are both editing this project — in pair programming,` +
      ' one person drives at a time.',
    [
      ['Take over as driver', 'Keep editing; your version saves now', opts.onTakeOver],
      [
        'Keep navigating',
        `Watch ${opts.partnerLabel} work — your version is kept on this computer`,
        opts.onNavigate,
      ],
    ],
    false,
  );
};

// The persistent curtain while the partner drives: the project follows their
// saves underneath (the IDE stays clean under the curtain, so the freshness
// poll keeps loading them) until this student takes over.
export const showNavigatorCurtain = (opts: { partnerLabel: string; onTakeOver: () => void }) => {
  driverOverlay(
    `${opts.partnerLabel} is driving — you're navigating. The project updates as they save.`,
    [['Take over as driver', 'Start editing from their latest version', opts.onTakeOver]],
    true,
  );
};

const driverOverlay = (
  text: string,
  buttons: [label: string, title: string, onPick: () => void][],
  navigating: boolean,
) => {
  dismissDriverOverlay();
  const container = $('.snap-frame-container') as HTMLElement | null;
  if (!container) return;

  const overlay = document.createElement('div');
  overlay.className = navigating ? 'pair-driver-overlay navigating' : 'pair-driver-overlay';
  const dialog = document.createElement('div');
  dialog.className = 'pair-driver-dialog';
  const message = document.createElement('p');
  message.textContent = text;
  const row = document.createElement('div');
  row.className = 'buttons';
  for (const [label, title, onPick] of buttons) {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = label;
    button.title = title;
    button.onclick = () => {
      dismissDriverOverlay();
      onPick();
    };
    row.append(button);
  }
  dialog.append(message, row);
  overlay.append(dialog);
  container.append(overlay);
};

export const dismissDriverOverlay = () => {
  document.querySelector('.pair-driver-overlay')?.remove();
};

/*
 * The stash-recovery banner. "Load their version" keeps the student's own
 * unsaved version in localStorage (the conflict-stash key) — this is the
 * matching way back, offered on the next page load, so the stash is a real
 * safety net rather than a write-only hole. Restore feeds the stashed
 * content back through the caller (who puts it in the editor and saves it —
 * in pair mode that save can 409 into the ordinary conflict flow, which is
 * exactly right); both choices clear the stash.
 */
export const showStashBanner = (opts: { onRestore: () => void; onDiscard: () => void }) => {
  const page = ($('.editor-page') ?? $('.snap-frame-container')) as HTMLElement | null;
  if (!page) return;

  const banner = document.createElement('div');
  banner.className = 'pair-conflict-banner stash-banner';

  const message = document.createElement('span');
  message.textContent =
    'You have an unsaved version of your work stashed on this computer from an earlier' +
    ' session, set aside when your partner’s version was kept.';
  banner.append(message);

  const restore = document.createElement('button');
  restore.type = 'button';
  restore.textContent = 'Restore it';
  restore.onclick = () => {
    banner.remove();
    opts.onRestore();
  };

  const discard = document.createElement('button');
  discard.type = 'button';
  discard.textContent = 'Discard it';
  discard.onclick = () => {
    banner.remove();
    opts.onDiscard();
  };

  banner.append(restore, discard);
  page.before(banner);
};
